/**
 * Default Theme Configuration
 * Base theme values that can be customized
 */

import { readableOn } from './contrast';
import {
  EmbedTheme,
  ThemeAnimation,
  ThemeBorderRadius,
  ThemeBorderWidth,
  ThemeComponentStyles,
  ThemeFontSize,
  ThemeFontWeight,
  ThemeLineHeight,
  ThemeShadow,
  ThemeSpacing,
  ThemeTypography,
} from './types';

// ============================================================================
// Shared Values (Mode Independent)
// ============================================================================

export const DEFAULT_PRIMARY_COLOR = '#3b82f6';

export const FONT_FAMILY =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export const FONT_SIZE: ThemeFontSize = {
  xs: '11px',
  sm: '12px',
  base: '14px',
  lg: '16px',
  xl: '18px',
  '2xl': '20px',
  '3xl': '24px',
};

export const FONT_WEIGHT: ThemeFontWeight = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
};

export const LINE_HEIGHT: ThemeLineHeight = {
  tight: 1.25,
  normal: 1.5,
  relaxed: 1.75,
};

export const TYPOGRAPHY: ThemeTypography = {
  fontFamily: FONT_FAMILY,
  fontSize: FONT_SIZE,
  fontWeight: FONT_WEIGHT,
  lineHeight: LINE_HEIGHT,
};

export const SPACING: ThemeSpacing = {
  none: '0px',
  '0.5': '2px',
  '1': '4px',
  '1.5': '6px',
  '2': '8px',
  '2.5': '10px',
  '3': '12px',
  '4': '16px',
  '5': '20px',
  '6': '24px',
  '8': '32px',
  '10': '40px',
  '12': '48px',
};

export const BORDER_RADIUS: ThemeBorderRadius = {
  none: '0px',
  sm: '4px',
  base: '6px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  '2xl': '20px',
  full: '9999px',
};

export const BORDER_WIDTH: ThemeBorderWidth = {
  none: '0px',
  thin: '1px',
  base: '1px',
  thick: '2px',
};

export const ANIMATION: ThemeAnimation = {
  duration: {
    fast: '150ms',
    normal: '200ms',
    slow: '300ms',
  },
  easing: {
    default: 'ease',
    easeIn: 'ease-in',
    easeOut: 'ease-out',
    easeInOut: 'ease-in-out',
  },
};

export const COMPONENT_STYLES: ThemeComponentStyles = {
  button: {
    height: {
      sm: '32px',
      md: '40px',
      lg: '48px',
    },
    padding: {
      sm: '6px 12px',
      md: '8px 16px',
      lg: '12px 24px',
    },
    borderRadius: BORDER_RADIUS.md,
  },
  input: {
    height: '44px',
    padding: '12px 16px',
    borderRadius: BORDER_RADIUS.lg,
  },
  card: {
    padding: '16px',
    borderRadius: BORDER_RADIUS.xl,
  },
  avatar: {
    sm: '24px',
    md: '32px',
    lg: '40px',
    xl: '80px',
  },
};

// ============================================================================
// Light Mode Colors
// ============================================================================

export const LIGHT_SHADOW: ThemeShadow = {
  none: 'none',
  sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
  base: '0 2px 8px rgba(0, 0, 0, 0.08)',
  md: '0 4px 12px rgba(0, 0, 0, 0.1)',
  lg: '0 8px 24px rgba(0, 0, 0, 0.12)',
  xl: '0 20px 40px rgba(0, 0, 0, 0.15)',
};

export function createLightTheme(primaryColor: string = DEFAULT_PRIMARY_COLOR): EmbedTheme {
  return {
    mode: 'light',
    colors: {
      palette: {
        primary: primaryColor,
        primaryHover: adjustColorBrightness(primaryColor, -10),
        primaryLight: `${primaryColor}20`,
        primarySubtle: `${primaryColor}10`,
      },
      background: {
        page: '#ffffff',
        surface: '#ffffff',
        elevated: '#ffffff',
        subtle: '#f9fafb',
        input: '#f3f4f6',
        hover: '#f3f4f6',
      },
      text: {
        primary: '#1f2937',
        // Secondary and tertiary carry real content (timestamps, sources,
        // suggestions): both reach WCAG AA (4.5:1) on every light surface.
        secondary: '#4b5563',
        tertiary: '#6b7280',
        inverse: '#ffffff',
        link: readableOn(primaryColor, ['#ffffff', '#f9fafb', '#f3f4f6']),
        error: '#dc2626',
        success: '#16a34a',
      },
      border: {
        default: '#e5e7eb',
        subtle: '#f3f4f6',
        strong: '#d1d5db',
        focus: primaryColor,
        error: '#fca5a5',
      },
    },
    typography: TYPOGRAPHY,
    spacing: SPACING,
    borderRadius: BORDER_RADIUS,
    borderWidth: BORDER_WIDTH,
    shadow: LIGHT_SHADOW,
    animation: ANIMATION,
    components: COMPONENT_STYLES,
  };
}

// ============================================================================
// Dark Mode Colors
// ============================================================================

export const DARK_SHADOW: ThemeShadow = {
  none: 'none',
  sm: '0 1px 2px rgba(0, 0, 0, 0.3)',
  base: '0 2px 8px rgba(0, 0, 0, 0.4)',
  md: '0 4px 12px rgba(0, 0, 0, 0.5)',
  lg: '0 8px 24px rgba(0, 0, 0, 0.6)',
  xl: '0 20px 40px rgba(0, 0, 0, 0.7)',
};

export function createDarkTheme(primaryColor: string = DEFAULT_PRIMARY_COLOR): EmbedTheme {
  return {
    mode: 'dark',
    colors: {
      palette: {
        primary: primaryColor,
        primaryHover: adjustColorBrightness(primaryColor, 10),
        primaryLight: `${primaryColor}30`,
        primarySubtle: `${primaryColor}15`,
      },
      background: {
        page: '#111827',
        surface: '#1f2937',
        elevated: '#374151',
        subtle: '#1f2937',
        input: '#374151',
        hover: '#374151',
      },
      text: {
        primary: '#f9fafb',
        // Readable on the answer bubble (#374151) as well as the page: AA 4.5:1.
        secondary: '#d1d5db',
        tertiary: '#a8b0bd',
        inverse: '#1f2937',
        link: readableOn(primaryColor, ['#1f2937', '#374151']),
        error: '#f87171',
        success: '#4ade80',
      },
      border: {
        default: '#374151',
        subtle: '#1f2937',
        strong: '#4b5563',
        focus: primaryColor,
        error: '#991b1b',
      },
    },
    typography: TYPOGRAPHY,
    spacing: SPACING,
    borderRadius: BORDER_RADIUS,
    borderWidth: BORDER_WIDTH,
    shadow: DARK_SHADOW,
    animation: ANIMATION,
    components: COMPONENT_STYLES,
  };
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Adjusts color brightness by a percentage
 * @param hex - Hex color string
 * @param percent - Positive to lighten, negative to darken
 */
function adjustColorBrightness(hex: string, percent: number): string {
  // Remove # if present
  const cleanHex = hex.replace('#', '');

  // Parse RGB values
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);

  // Adjust brightness
  const adjust = (value: number) => {
    const adjusted = value + (value * percent) / 100;
    return Math.min(255, Math.max(0, Math.round(adjusted)));
  };

  // Convert back to hex
  const toHex = (value: number) => value.toString(16).padStart(2, '0');

  return `#${toHex(adjust(r))}${toHex(adjust(g))}${toHex(adjust(b))}`;
}

/**
 * Creates a theme based on mode and primary color
 */
export function createTheme(
  mode: 'light' | 'dark' = 'light',
  primaryColor: string = DEFAULT_PRIMARY_COLOR,
): EmbedTheme {
  return mode === 'dark' ? createDarkTheme(primaryColor) : createLightTheme(primaryColor);
}

/**
 * Default light theme instance
 */
export const defaultLightTheme = createLightTheme();

/**
 * Default dark theme instance
 */
export const defaultDarkTheme = createDarkTheme();
